import { DocumentTextIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { DocumentFileItem, DocumentUpload } from '@ths/shared/components';
import { useState, useRef, useEffect } from 'react';

import { RosterRequestFile } from '../../generated/graphql';
import { useRosterRequestFileUpload } from '../../hooks/useRosterRequestFileUpload';
import {
  DOCUMENTS_ACCEPT,
  formatFileSize,
  getFileIcon,
} from '../../utilities/fileUtils';
import { ActionsMenu } from '../ActionsMenu';

interface RequestFilesSectionProps {
  rosterRequestId: string;
  files: RosterRequestFile[];
  onFilesChanged?: () => void;
  readOnly?: boolean;
}

export function RequestFilesSection({
  rosterRequestId,
  files,
  onFilesChanged,
  readOnly = false,
}: RequestFilesSectionProps) {
  const [uploadState, setUploadState] = useState<
    'idle' | 'uploading' | 'success'
  >('idle');
  const [successMessage, setSuccessMessage] = useState('');
  const successTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
    };
  }, []);

  const { uploadFile, deleteFile, viewFile, isUploading } =
    useRosterRequestFileUpload({
      rosterRequestId,
      onUploadComplete: onFilesChanged,
      onDeleteComplete: onFilesChanged,
    });

  const handleConfirmUpload = async (file: File, displayName: string) => {
    setUploadState('uploading');

    try {
      await uploadFile(file, displayName);

      setSuccessMessage('File uploaded successfully');
      setUploadState('success');

      successTimeoutRef.current = setTimeout(() => {
        setUploadState('idle');
        setSuccessMessage('');
      }, 1500);
    } catch {
      setUploadState('idle');
      throw new Error('Upload failed');
    }
  };

  const handleViewFile = (fileId: string) => {
    viewFile(fileId);
  };

  const handleDeleteFile = async (fileId: string) => {
    if (window.confirm('Are you sure you want to delete this file?')) {
      await deleteFile(fileId);
    }
  };

  return (
    <div className="space-y-4">
      <span className="text-sm font-medium text-gray-700">Attachments</span>

      <div className="space-y-4">
        {/* File List */}
        {files && files.length > 0 ? (
          <div className="rounded-lg border border-gray-200 bg-white">
            <ul className="divide-y divide-gray-200">
              {files.map((file) => (
                <li
                  key={file.id}
                  className="px-5 py-3.5 transition-colors hover:bg-gray-50"
                >
                  <DocumentFileItem
                    file={file}
                    canEdit={!readOnly}
                    onView={() => handleViewFile(file.id)}
                    onDelete={() => handleDeleteFile(file.id)}
                    ActionsMenu={ActionsMenu}
                  />
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="rounded-lg border border-gray-200 bg-white px-6 py-8">
            <div className="text-center">
              <DocumentTextIcon className="mx-auto size-10 text-gray-300" />
              <p className="mt-2 text-sm text-gray-500">
                No files attached yet
              </p>
            </div>
          </div>
        )}

        {/* Upload Section */}
        {!readOnly && (
          <>
            {uploadState === 'success' ? (
              <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-green-200 bg-green-50/30 py-8">
                <div className="mb-3 flex size-10 items-center justify-center rounded-full bg-green-100">
                  <CheckCircleIcon className="size-5 text-green-600" />
                </div>
                <span className="text-sm font-medium text-slate-900">
                  {successMessage}
                </span>
              </div>
            ) : (
              <DocumentUpload
                accept={`${DOCUMENTS_ACCEPT},image/*`}
                maxSize={100}
                description="PDF, Word, Excel, text, and image files up to 100MB"
                onConfirm={handleConfirmUpload}
                isUploading={uploadState === 'uploading' || isUploading}
                getFileIcon={getFileIcon}
                formatFileSize={formatFileSize}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
